/**
 * Estate URL Utilities
 *
 * URL format: /estates/{slug}-{public_id}
 *
 * The slug-segment compose/extract + `PUBLIC_ID_LENGTH` are the GENERATED
 * `slug-url.gen` module (routes-SSOT ADR 0142) — the web mirror of the
 * `slug_url` Rust crate, the single cross-Trinity implementation the backend
 * and mobile also consume. This file only adds the React-Router route wrapping.
 */

import { composeSegment, extractPublicId, PUBLIC_ID_LENGTH } from '@/shared/lib/url/slug-url.gen';
import { routes, type Route } from '@letsgethome/routes';
import { slug as toSlug, type PublicId, type Slug } from '@letsgethome/types/branded';

// Re-export the SSOT primitives so existing import sites keep working.
export { PUBLIC_ID_LENGTH, extractPublicId };

/**
 * Build an estate URL from slug and public_id (`{slug}-{publicId}`, or just the
 * public id when the slug is empty).
 *
 * @example
 * buildEstateUrl('tai-koo-shing', 'abc123def456') // '/estates/tai-koo-shing-abc123def456'
 * buildEstateUrl('', 'abc123def456') // '/estates/abc123def456'
 */
export function buildEstateUrl(slug: Slug | undefined, publicId: PublicId): Route {
  return routes.estates.detail(toSlug(composeSegment(slug ?? '', publicId)));
}

/**
 * Build an estate URL from an estate object that has slug and publicId.
 * Falls back to just publicId if slug is empty.
 */
export function getEstateHref(estate: { slug?: Slug; publicId: PublicId }): Route {
  return buildEstateUrl(estate.slug, estate.publicId);
}

/**
 * Build an estate slug segment for URL construction (`{slug}-{publicId}`).
 */
export function buildEstateSlugSegment(slug: Slug | undefined, publicId: PublicId): string {
  return composeSegment(slug ?? '', publicId);
}
